import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { isAdmin, getUserRole, hasAccessToSecretariaId } from "./Auth";

// Hook customizado para "debounce" (atrasar a busca)
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};


function ViewOrcamento() {
  // ----- ESTADOS -----
  const [orcamentos, setOrcamentos] = useState([]); // Renomeado de 'data'
  const [userRole, setUserRole] = useState("");
  
  // Estado da Busca
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Valor enviado à API

  // Estado da Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // ----- CARREGAMENTO DE DADOS -----
  // Usamos useCallback para que a função não seja recriada a cada render
  // e possa ser chamada com segurança pelo useEffect e handleDelete
  const carregarDados = useCallback(() => {
    const params = new URLSearchParams({
      page: currentPage,
      limit: 15, // Você pode ajustar este limite
    });

    if (debouncedSearchTerm) {
      params.append("search", debouncedSearchTerm);
    }
    
    // NOTA: Se você tiver outros <select> de filtro (ex: por 'aplicacao'),
    // você adicionaria eles aqui também:
    // if (filtroAplicacao) {
    //   params.append("aplicacao", filtroAplicacao);
    // }

    axios
      .get(`/api/orcamento?${params.toString()}`)
      .then((res) => {
        if (res.data.Status === "Success") {
          setOrcamentos(res.data.Result);
          setPaginationInfo(res.data.Pagination);
        } else {
          alert("Erro ao carregar dados");
        }
      })
      .catch((err) => console.error(err));
  }, [currentPage, debouncedSearchTerm]); // Recria a função se a página ou a busca mudarem

  // Efeito principal para buscar dados
  useEffect(() => {
    setUserRole(getUserRole());
    carregarDados();
  }, [carregarDados]); // Roda o efeito quando a função 'carregarDados' mudar

  
  // ----- HANDLERS (Manipuladores) -----
  
  const handleDelete = (id) => {
    if (window.confirm("Tem certeza que deseja deletar este lançamento?")) {
      axios
        .delete(`/api/orcamento/${id}`)
        .then((res) => {
          if (res.data.message === "Orçamento deletado com sucesso!") {
            alert("Lançamento deletado!");
            
            // Lógica para recarregar os dados:
            // Se for o último item da página atual (e não for a página 1),
            // volte para a página anterior.
            if (orcamentos.length === 1 && currentPage > 1) {
              setCurrentPage(currentPage - 1);
            } else {
              // Caso contrário, apenas recarregue os dados da página atual
              carregarDados();
            }
          } else {
            alert("Falha ao deletar o lançamento.");
          }
        })
        .catch(() => alert("Erro no servidor"));
    }
  };

  // Atualiza o termo de busca e volta para a página 1
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); 
  };

  // Funções de navegação da paginação
  const goToNextPage = () => {
    if (currentPage < paginationInfo.totalPages) {
      setCurrentPage((page) => page + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((page) => page - 1);
    }
  };

  // ----- RENDERIZAÇÃO -----
  return (
    <div className="fs-11 px-18 py-8" style={{ overflow: "visible" }}>
      <div className="d-flex justify-content-center mb-3">
        <h3>Lista de Lançamentos Orçamentários</h3>
      </div>

      <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
        {(isAdmin() || userRole) && (
          <button className="btn btn-success" onClick={() => { /* Navegar para criar */ }}>
            + Novo Lançamento
          </button>
        )}
        <button
          className="btn btn-secondary"
          onClick={() => window.history.back()}
        >
          Voltar
        </button>

        <input
          type="text"
          placeholder="Buscar por Secretaria, Conta, Ficha..."
          className="form-control"
          style={{ maxWidth: "300px" }}
          value={searchTerm}
          onChange={handleSearchChange} // <--- ATUALIZADO
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="table table-responsive table-bordered table-hover fs-10">
          <thead className="table-danger">
            <tr>
              <th>Secretaria</th>
              <th>Funcional</th>
              <th>Classificação</th>
              <th>Conta / Elemento</th>
              <th>Aplicação</th>
              <th>Fonte STN</th>
              <th>Fonte</th>
              <th>Ficha</th>
              <th>Valor (R$)</th>
              {(isAdmin() || userRole) && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {/* REMOVEMOS O .filter() DAQUI!
              Agora mapeamos 'orcamentos' (que são os dados da página atual)
            */}
            {orcamentos.length === 0 ? (
              <tr>
                <td colSpan={isAdmin() || userRole ? 10 : 9} className="text-center">
                  Nenhum lançamento encontrado.
                </td>
              </tr>
            ) : (
              orcamentos.map((orcamento, index) => { // <--- ATUALIZADO
                const podeEditarExcluir = hasAccessToSecretariaId(
                  orcamento.secretaria_id
                );

                return (
                  <tr key={orcamento.id || index}> {/* Use orcamento.id se for único */}
                    <td>{orcamento.secretaria_nome}</td>
                    <td>{orcamento.funcional_programatica}</td>
                    <td>{orcamento.classificacao_institucional}</td>
                    <td>{orcamento.conta_elemento_despesa}</td>
                    <td>{orcamento.aplicacao}</td>
                    <td>{orcamento.fonte_recurso_stn}</td>
                    <td>{orcamento.fonte_recurso}</td>
                    <td>{orcamento.ficha}</td>
                    <td>
                      {orcamento.valor
                        ? Number(orcamento.valor).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })
                        : ""}
                    </td>
                    {(isAdmin() || userRole) && (
                      <td>
                        {podeEditarExcluir ? (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(orcamento.id)}
                          >
                            Deletar
                          </button>
                        ) : (
                          <em>Sem permissão</em>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ----- CONTROLES DE PAGINAÇÃO ----- */}
      {paginationInfo && paginationInfo.totalPages > 1 && (
        <nav className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <span className="text-muted small">
              Página {paginationInfo.page} de {paginationInfo.totalPages} 
              (Total: {paginationInfo.totalItems} lançamentos)
            </span>
          </div>
          <div>
            <button 
              className="btn btn-outline-primary btn-sm me-2" 
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={goToNextPage}
              disabled={currentPage === paginationInfo.totalPages}
            >
              Próxima
            </button>
          </div>
        </nav>
      )}

    </div>
  );
}

export default ViewOrcamento;